import { type PlatformLanguage } from '~/utils/types/i18n'

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export default class DateUtils {
  /**
   * @param {number} unixDate - A valid unix date to convert to a full format
   * @returns {string} A formatted string in full format (Tue May 12 2020)
   *
   * Note: In some locales, the implementation is set to return month names in lowercase. If you need to display the date capitalized, use CSS to do so.
   */
  static unixToFullFormat(unixDate: number, locale: PlatformLanguage): string {
    const UNIX_TO_SECONDS_MULTIPLIER = 1000
    const date = new Date(unixDate * UNIX_TO_SECONDS_MULTIPLIER)

    try {
      return date.toLocaleDateString(locale, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      })
    } catch (error) {
      if (error instanceof RangeError) return error.message
      return 'Invalid Date'
    }
  }

  /**
   * @param {Date | string} date - A valid date object or ISO date string to format
   * @param {PlatformLanguage} locale - The locale to use for formatting
   * @returns {string} A formatted string in short format (MMM d, yyyy)
   *
   * Note: In some locales, the implementation is set to return month names in lowercase. If you need to display the date capitalized, use CSS to do so.
   */
  static toShortDateFormat(
    date: Date | string,
    locale: PlatformLanguage,
  ): string {
    const dateObj = date instanceof Date ? date : new Date(date)

    try {
      return dateObj.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    } catch (error) {
      if (error instanceof RangeError) return error.message
      return 'Invalid Date'
    }
  }
}
